/**
 * The single definition of the TDL domain.
 *
 * Both the server and the client import from this file, so a schema change
 * surfaces as a compile error on the other side rather than a runtime surprise.
 * Keep it dependency-free — it must be importable from Node and the browser.
 */

/** Task lifecycle. `inbox` is "captured but not yet triaged". */
export const TASK_STATUSES = [
  'inbox',
  'todo',
  'in_progress',
  'blocked',
  'done',
  'cancelled',
] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

/** Statuses that mean "off my plate" — excluded from Today/Upcoming by default. */
export const CLOSED_STATUSES: readonly TaskStatus[] = ['done', 'cancelled'];

/** 0 = none, 3 = high. Numeric so it sorts in SQL without a CASE expression. */
export const PRIORITIES = [0, 1, 2, 3] as const;
export type Priority = (typeof PRIORITIES)[number];

export const PRIORITY_LABELS: Record<Priority, string> = {
  0: 'None',
  1: 'Low',
  2: 'Medium',
  3: 'High',
};

export const PROJECT_STATUSES = ['active', 'paused', 'done'] as const;
export type ProjectStatus = (typeof PROJECT_STATUSES)[number];

export const VIEW_KINDS = ['list', 'table', 'board'] as const;
export type ViewKind = (typeof VIEW_KINDS)[number];

/**
 * All timestamps are UTC ISO-8601 strings (`2026-08-05T14:30:00.000Z`).
 * SQLite has no date type; storing ISO strings keeps them sortable as text.
 */
export type Timestamp = string;

export type { RecurFreq, RecurFrom, Recurrence } from './recurrence.ts';
import type { RecurFreq, RecurFrom } from './recurrence.ts';

export interface Area {
  id: string;
  name: string;
  position: number;
  created_at: Timestamp;
}

export interface Project {
  id: string;
  name: string;
  /** Reserved for a future Areas UI. Null for every project today. */
  area_id: string | null;
  color: string;
  status: ProjectStatus;
  position: number;
  archived_at: Timestamp | null;
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface Tag {
  id: string;
  name: string;
  color: string;
}

export interface Task {
  id: string;
  title: string;
  /** TipTap document JSON. Null until the task has notes. */
  notes_json: string | null;
  /** Flattened plain text of notes_json — this is what FTS indexes. */
  notes_text: string | null;
  status: TaskStatus;
  priority: Priority;
  project_id: string | null;
  /** One level of subtasks only; a subtask may not itself have children. */
  parent_id: string | null;
  due_at: Timestamp | null;
  start_at: Timestamp | null;
  estimate_minutes: number | null;
  /** Float so a reorder writes one row instead of renumbering the list. */
  position: number;
  completed_at: Timestamp | null;
  archived_at: Timestamp | null;
  created_at: Timestamp;
  updated_at: Timestamp;

  /** Null when the task does not repeat. See shared/recurrence.ts. */
  recur_freq: RecurFreq | null;
  recur_interval: number;
  recur_from: RecurFrom;
  /** The occurrence this one was spawned from, so a series can be followed. */
  recur_parent_id: string | null;
}

/** A task as the API returns it: joined with its tags and subtask rollup. */
export interface TaskWithRelations extends Task {
  tags: Tag[];
  subtask_count: number;
  subtask_done_count: number;
}

export interface View {
  id: string;
  name: string;
  kind: ViewKind;
  filter_json: string;
  sort_json: string;
  group_by: string | null;
  position: number;
}

// ---------------------------------------------------------------------------
// Filtering
// ---------------------------------------------------------------------------

/**
 * A saved view's filter. Every field is optional and they AND together.
 * Deliberately a fixed shape rather than an arbitrary expression tree — it
 * covers the real queries and compiles to SQL that stays readable.
 */
export interface TaskFilter {
  status?: TaskStatus[];
  priority?: Priority[];
  project_id?: (string | null)[];
  tag_ids?: string[];
  /** Match tasks carrying ALL of tag_ids rather than any. */
  tags_match_all?: boolean;
  due_before?: Timestamp;
  due_after?: Timestamp;
  /** Tasks with no due date at all. */
  no_due_date?: boolean;
  /** Untouched since this instant — how the weekly review finds stale work. */
  updated_before?: Timestamp;
  /** Finished on or after this instant, for "what did I get done". */
  completed_after?: Timestamp;
  /** Full-text query across title and notes. */
  q?: string;
  /** Top-level tasks only (parent_id IS NULL). Defaults to true in list views. */
  top_level_only?: boolean;
  include_archived?: boolean;
}

export const SORTABLE_FIELDS = [
  'position',
  'title',
  'status',
  'priority',
  'due_at',
  'created_at',
  'updated_at',
  'completed_at',
  'estimate_minutes',
] as const;
export type SortableField = (typeof SORTABLE_FIELDS)[number];

export interface TaskSort {
  field: SortableField;
  dir: 'asc' | 'desc';
}

/**
 * Fields the table can group rows by. `due` is a bucket (Overdue / Today /
 * This week / Later / No date) rather than the raw timestamp — grouping by an
 * exact instant would put every task in its own group.
 */
export const GROUPABLE_FIELDS = ['project_id', 'status', 'priority', 'due'] as const;
export type GroupableField = (typeof GROUPABLE_FIELDS)[number];

// ---------------------------------------------------------------------------
// API payloads
// ---------------------------------------------------------------------------

export interface CreateTaskInput {
  title: string;
  status?: TaskStatus;
  priority?: Priority;
  project_id?: string | null;
  parent_id?: string | null;
  due_at?: Timestamp | null;
  start_at?: Timestamp | null;
  estimate_minutes?: number | null;
  notes_json?: string | null;
  notes_text?: string | null;
  tag_ids?: string[];
  /**
   * Tag names from quick capture (`#close`). The server creates any that do not
   * exist yet, so capture stays a single request instead of a lookup round-trip
   * followed by a create.
   */
  tag_names?: string[];
  /** Project name from quick capture (`+Budget`). Matched case-insensitively. */
  project_name?: string | null;

  recur_freq?: RecurFreq | null;
  recur_interval?: number;
  recur_from?: RecurFrom;
}

export type UpdateTaskInput = Partial<CreateTaskInput>;

/** One drag on the board: where the task landed and what changed with it. */
export interface ReorderTaskInput {
  id: string;
  position: number;
  status?: TaskStatus;
  project_id?: string | null;
}

export interface AuthState {
  authenticated: boolean;
  /** False before the first password is set — the client shows setup instead of login. */
  configured: boolean;
}

export interface ApiError {
  error: string;
  detail?: string;
}
